import { useState, useEffect, useRef, useCallback } from "react";
import SidebarGastos from "./SidebarGastos";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import { useAuth } from "../context/AuthContext";

/* ─── fila vacía ─── */
const FILA_NUEVA = {
  descripcion: "",
  noParte: "",
  cantidad: 0,
  precioUnitario: 0,
  createdAt: serverTimestamp(),
  listaId: "",
  listaNombre: "",
};

/* ─── helpers ─── */
const genColor = (i) => `hsl(${(i * 137.508) % 360} 72% 55%)`;
const ssKey = (o, k) => `gastosView:${o}:${k}`;
const ssGet = (k, def) => {
  try {
    return JSON.parse(sessionStorage.getItem(k) ?? JSON.stringify(def));
  } catch {
    return def;
  }
};
const ssSet = (k, v) => {
  try {
    sessionStorage.setItem(k, JSON.stringify(v));
  } catch {}
};

export default function GastosView({ ordenId }) {
  const { formatDate, formatCurrency, trimLeadingZeros, calcDashboard } =
    useFunctionsPrevia();
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const [dash, setDash] = useState({
    totalCarpetas: 0,
    ingresos: 0,
    gastos: 0,
    balance: 0,
  });

  const [showList, setShowList] = useState(false);
  const [listas, setListas] = useState([]);
  const [listaSel, setListaSel] = useState(null);
  const [modalProds, setModalProds] = useState([]);
  const [productos, setProductos] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [movs, setMovs] = useState([]);
  const listeners = useRef({});
  const firstLoadOK = useRef({});
  const masterProd = useRef([]);
  const colorMap = useRef({});
  const activeLista = useRef(ssGet(ssKey(ordenId, "activeLista"), null));
  const [showAddModal, setShowAddModal] = useState(false);
  const [targetLista, setTargetLista] = useState(""); // id carpeta destino
  const [newRow, setNewRow] = useState({
    descripcion: "",
    noParte: "",
    cantidad: 0,
    precioUnitario: 0,
  });
  const refreshDashboard = useCallback(
    () => setDash(calcDashboard(masterProd.current, listas)),
    [calcDashboard, listas]
  );

  useEffect(() => {
    if (listas.length) refreshDashboard(); // ← NUEVO
  }, [listas, refreshDashboard]);

  const clearNewRow = () =>
    setNewRow({ descripcion: "", noParte: "", cantidad: 0, precioUnitario: 0 });

  /* ═════════ reconstruir dashboard ═════════ */
  const rebuildChartAndMovs = useCallback(() => {
    const fuente = activeLista.current
      ? masterProd.current.filter((p) => p.listaId === activeLista.current)
      : masterProd.current;

    /* --- gráficos --- */
    const byDesc = {};
    fuente.forEach(({ descripcion = "", cantidad = 0, precioUnitario = 0 }) => {
      const desc = descripcion.trim();
      const imp = cantidad * precioUnitario;
      if (!desc || !imp) return;
      byDesc[desc] = (byDesc[desc] || 0) + imp;
    });

    const ordered = Object.entries(byDesc)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const main = ordered.slice(0, 9);
    const rest = ordered.slice(9);
    if (rest.length)
      main.push({
        name: "Otros",
        value: rest.reduce((s, r) => s + r.value, 0),
      });

    main.forEach(({ name }) => {
      if (!colorMap.current[name])
        colorMap.current[name] = genColor(Object.keys(colorMap.current).length);
    });
    setPieData(main.map((d) => ({ ...d, color: colorMap.current[d.name] })));

    /* --- últimas transacciones --- */
    const ult = fuente
      .filter((m) => m.descripcion.trim() && m.cantidad * m.precioUnitario > 0)
      .sort((a, b) => {
        const ta =
          a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : 0;
        const tb =
          b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : 0;
        return tb - ta;
      })
      .slice(0, 10);

    setMovs(ult);
    refreshDashboard();
  }, [refreshDashboard]);

  /* ═════════ guardar showList ═════════ */
  useEffect(() => {
    if (!ownerUid || !ordenId) return;

    // Referencia a las carpetas de gastos bajo el usuario y su pedido
    const carpetasRef = collection(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos"
    );
    const unsubscribe = onSnapshot(carpetasRef, (snap) => {
      const carpetas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setListas(carpetas);

      /* ── Eliminar listeners obsoletos ── */
      Object.keys(listeners.current).forEach((id) => {
        if (!carpetas.find((c) => c.id === id)) {
          listeners.current[id]();
          delete listeners.current[id];
          delete firstLoadOK.current[id];
          masterProd.current = masterProd.current.filter(
            (p) => p.listaId !== id
          );
          if (activeLista.current === id) {
            activeLista.current = null;
            ssSet(ssKey(ordenId, "activeLista"), null);
          }
        }
      });

      /* ── Crear nuevos listeners para cada carpeta ── */
      carpetas.forEach(({ id }) => {
        if (listeners.current[id]) return;

        const productosRef = collection(
          db,
          "usuarios",
          ownerUid,
          "pedidos",
          ordenId,
          "listasGastos",
          id,
          "productosGastos"
        );
        listeners.current[id] = onSnapshot(productosRef, (snapProd) => {
          const alreadyInit = firstLoadOK.current[id];
          const hasRealChange = snapProd
            .docChanges()
            .some((ch) => ch.type !== "added" || alreadyInit);

          if (hasRealChange) {
            activeLista.current = id;
            ssSet(ssKey(ordenId, "activeLista"), id);
          }
          firstLoadOK.current[id] = true;

          masterProd.current = [
            ...masterProd.current.filter((p) => p.listaId !== id),
            ...snapProd.docs.map((d) => ({
              id: d.id,
              listaId: id,
              ...d.data(),
            })),
          ];
          rebuildChartAndMovs();
          refreshDashboard();
        });
      });
    });

    return unsubscribe;
  }, [ownerUid, ordenId, rebuildChartAndMovs, refreshDashboard]);

  /* ═════════ listeners de carpetas ═════════ */
  useEffect(() => {
    if (!ownerUid || !ordenId) return;

    // Carpeta de listas bajo usuarios/{ownerUid}/pedidos/{ordenId}/listasGastos
    const carpetasRef = collection(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos"
    );

    const unsubscribe = onSnapshot(carpetasRef, (snap) => {
      const carpetas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setListas(carpetas);

      /* ── Elimina listeners de carpetas que ya no existen ── */
      Object.keys(listeners.current).forEach((id) => {
        if (!carpetas.find((c) => c.id === id)) {
          listeners.current[id]();
          delete listeners.current[id];
          delete firstLoadOK.current[id];
          masterProd.current = masterProd.current.filter(
            (p) => p.listaId !== id
          );
          if (activeLista.current === id) {
            activeLista.current = null;
            ssSet(ssKey(ordenId, "activeLista"), null);
          }
        }
      });

      /* ── Crea listeners para cada carpeta nueva o vigente ── */
      carpetas.forEach(({ id }) => {
        if (listeners.current[id]) return;

        const productosRef = collection(
          db,
          "usuarios",
          ownerUid,
          "pedidos",
          ordenId,
          "listasGastos",
          id,
          "productosGastos"
        );
        listeners.current[id] = onSnapshot(productosRef, (snapProd) => {
          const alreadyInit = firstLoadOK.current[id];
          const hasRealChange = snapProd
            .docChanges()
            .some((ch) => ch.type !== "added" || alreadyInit);

          if (hasRealChange) {
            activeLista.current = id;
            ssSet(ssKey(ordenId, "activeLista"), id);
          }
          firstLoadOK.current[id] = true;

          masterProd.current = [
            ...masterProd.current.filter((p) => p.listaId !== id),
            ...snapProd.docs.map((d) => ({
              id: d.id,
              listaId: id,
              ...d.data(),
            })),
          ];
          rebuildChartAndMovs();
          refreshDashboard();
        });
      });
    });

    return unsubscribe;
  }, [ownerUid, ordenId, rebuildChartAndMovs, refreshDashboard]);

  /* ═════════ listener productos para la tabla ═════════ */
  useEffect(() => {
    if (!ownerUid || !ordenId || !listaSel?.id) {
      setProductos([]);
      return;
    }

    // Ruta completa incluyendo el ownerUid
    const productosRef = collection(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos",
      listaSel.id,
      "productosGastos"
    );

    // 📌 Orden ascendente por createdAt, dejando los docs “pendientes” al final
    const unsub = onSnapshot(productosRef, (snap) => {
      const getMs = (t) =>
        t instanceof Timestamp ? t.toMillis() : Number.MAX_SAFE_INTEGER;

      const prods = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => getMs(a.createdAt) - getMs(b.createdAt));

      setProductos(prods);
    });

    return unsub;
  }, [ownerUid, ordenId, listaSel?.id]);

  useEffect(() => {
    if (!ownerUid || !ordenId || !targetLista) {
      setModalProds([]);
      return;
    }

    // Ruta completa incluyendo el ownerUid
    const modalProdsRef = collection(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos",
      targetLista,
      "productosGastos"
    );

    const unsub = onSnapshot(modalProdsRef, (snap) => {
      const getMs = (t) => (t instanceof Timestamp ? t.toMillis() : 0);
      const arr = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => getMs(a.createdAt) - getMs(b.createdAt));

      setModalProds(arr);
    });

    return unsub;
  }, [ownerUid, ordenId, targetLista]);

  /* ═════════ selección inicial ═════════ */
  useEffect(() => {
    if (!listas.length) return;
    const prefer = activeLista.current || listaSel?.id || listas[0].id;
    if (listaSel?.id !== prefer) {
      setListaSel(listas.find((l) => l.id === prefer) ?? listas[0]);
    }
  }, [listas, listaSel?.id]);

  /* ═════════ CRUD helpers ═════════ */
  const afterWrite = (idCarp) => {
    activeLista.current = idCarp;
    ssSet(ssKey(ordenId, "activeLista"), idCarp);
    refreshDashboard(); // ← NUEVO
  };

  const addRow = async () => {
    if (!listaSel) return;
    // Aquí usamos ownerUid para componer la ruta completa
    const productosRef = collection(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos",
      listaSel.id,
      "productosGastos"
    );
    await addDoc(productosRef, {
      ...FILA_NUEVA,
      listaId: listaSel.id,
      listaNombre: listaSel.nombre,
      ownerId: ownerUid, // por si tus reglas lo requieren en el documento
    });
    afterWrite(listaSel.id);
  };

  const handleChange = async (i, field, val) => {
    const p = productos[i];
    if (!p) return;
    // referenciamos con ownerUid
    const gastoDoc = doc(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos",
      listaSel.id,
      "productosGastos",
      p.id
    );
    await updateDoc(gastoDoc, {
      [field]:
        field === "cantidad" || field === "precioUnitario"
          ? parseFloat(val) || 0
          : val,
      listaNombre: listaSel.nombre,
      // si quieres reforzar tu lógica de reglas:
      ownerId: ownerUid,
    });
    afterWrite(listaSel.id);
  };

  const removeRow = async (i) => {
    const p = productos[i];
    if (!p) return;
    // Construimos la referencia incluyendo ownerUid
    const gastoDoc = doc(
      db,
      "usuarios",
      ownerUid,
      "pedidos",
      ordenId,
      "listasGastos",
      listaSel.id,
      "productosGastos",
      p.id
    );
    await deleteDoc(gastoDoc);
    afterWrite(listaSel.id);
  };

  /* ═════════ RENDER – DASHBOARD ═════════ */
  if (!showList) {
    return (
      <div className="gastos-main">
        {/* ─── TOTAL ─── */}
        <div className="total-carpetas">
          Total en carpetas:&nbsp;
          <strong>{formatCurrency(dash.totalCarpetas)}</strong>
        </div>

        {/* ─── TARJETAS ─── */}
        <div className="summary-grid">
          {[
            { t: "Balance", v: dash.balance, up: dash.balance >= 0 },
            { t: "Ingresos", v: dash.ingresos, up: true },
            { t: "Gastos", v: dash.gastos, up: false },
          ].map(({ t, v, up }) => (
            <div key={t} className="summary-card">
              <div className="card-title">{t}</div>
              <div className="card-value">{formatCurrency(v)}</div>
              <div className={`card-change ${up ? "up" : "down"}`}>
                {up ? "▲" : "▼"} 12%
              </div>
            </div>
          ))}
        </div>

        {/* ─── CTA ─── */}
        <div className="cta-row">
          <div className="cta-card" onClick={() => setShowList(true)}>
            <i className="bi bi-folder2-open cta-icon" />
            <div>
              <div className="cta-title">Gastos</div>
              <div className="cta-sub">Haz click para ver carpetas</div>
            </div>
          </div>
          <div
            className={`cta-card ${listas.length ? "" : "disabled"}`}
            onClick={() => listas.length && setShowAddModal(true)}
          >
            <i className="bi bi-plus-square cta-icon cta-icon-add" />
            <div>
              <div className="cta-title">Agregar gastos</div>
              <div className="cta-sub">
                {listas.length
                  ? "Crea un gasto manualmente"
                  : "Crea una carpeta primero"}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Modal “Agregar gasto” ─── */}
        {showAddModal && (
          <div className="modal-mask">
            <div className="modal-box">
              <h3>Agregar gasto</h3>

              {/* carpeta destino */}
              <label className="modal-label">Carpeta destino</label>
              <select
                value={targetLista}
                onChange={(e) => setTargetLista(e.target.value)}
                className="modal-input"
              >
                <option value="">-- Selecciona carpeta --</option>
                {listas.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nombre}
                  </option>
                ))}
              </select>

              {/* fila de captura */}
              <table
                className="modal-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 20,
                  tableLayout: "fixed",
                  fontSize: 14,
                }}
              >
                {/* Anchuras fijas para que nada se salga */}
                <colgroup>
                  <col style={{ width: "5%" }} /> {/* # */}
                  <col style={{ width: "30%" }} /> {/* Descripción */}
                  <col style={{ width: "18%" }} /> {/* No. parte    */}
                  <col style={{ width: "11%" }} /> {/* Cantidad     */}
                  <col style={{ width: "18%" }} /> {/* Precio unit. */}
                  <col style={{ width: "18%" }} /> {/* Sub-total    */}
                </colgroup>

                <thead>
                  <tr
                    style={{
                      background: "#f5f5f7",
                      textTransform: "uppercase",
                    }}
                  >
                    {[
                      "#",
                      "Descripción",
                      "No. parte",
                      "Cantidad",
                      "Precio unit.",
                      "Subtotal",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "8px 6px",
                          textAlign: i === 5 ? "right" : "left",
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* filas existentes (solo lectura) */}
                  {modalProds.map((p, i) => (
                    <tr key={p.id} style={{ opacity: 0.6 }}>
                      <td style={{ textAlign: "center" }}>{i + 1}</td>
                      <td>{p.descripcion}</td>
                      <td>{p.noParte}</td>
                      <td style={{ textAlign: "right" }}>{p.cantidad}</td>
                      <td style={{ textAlign: "right" }}>
                        {formatCurrency(p.precioUnitario)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {formatCurrency(p.cantidad * p.precioUnitario)}
                      </td>
                    </tr>
                  ))}

                  {/* fila vacía para capturar un nuevo gasto */}
                  <tr>
                    <td style={{ textAlign: "center" }}>
                      {modalProds.length + 1}
                    </td>

                    <td>
                      <input
                        style={{ width: "100%" }}
                        value={newRow.descripcion}
                        onChange={(e) =>
                          setNewRow({ ...newRow, descripcion: e.target.value })
                        }
                      />
                    </td>

                    <td>
                      <input
                        style={{ width: "100%" }}
                        value={newRow.noParte}
                        onChange={(e) =>
                          setNewRow({ ...newRow, noParte: e.target.value })
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        style={{ width: "100%", textAlign: "right" }}
                        value={newRow.cantidad}
                        onChange={(e) =>
                          setNewRow({
                            ...newRow,
                            cantidad: +e.target.value || 0,
                          })
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        style={{ width: "100%", textAlign: "right" }}
                        value={newRow.precioUnitario}
                        onChange={(e) =>
                          setNewRow({
                            ...newRow,
                            // elimina ceros a la izquierda, luego convierte a número
                            precioUnitario: +trimLeadingZeros(e.target.value),
                          })
                        }
                      />
                    </td>

                    <td style={{ textAlign: "right", fontWeight: 500 }}>
                      {formatCurrency(newRow.cantidad * newRow.precioUnitario)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* botones */}
              <div className="modal-actions">
                <button
                  className="secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    clearNewRow();
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="primary"
                  onClick={async () => {
                    if (!targetLista) {
                      alert("Selecciona una carpeta destino");
                      return;
                    }
                    await addDoc(
                      collection(
                        db,
                        `pedidos/${ordenId}/listasGastos/${targetLista}/productosGastos`
                      ),
                      {
                        ...newRow,
                        listaId: targetLista,
                        listaNombre:
                          listas.find((l) => l.id === targetLista)?.nombre ||
                          "",
                        createdAt: serverTimestamp(),
                      }
                    );
                    clearNewRow();
                    setShowAddModal(false);
                  }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── GRÁFICA + MOVS ─── */}
        <div className="chart-grid gastos-dashboard">
          <div className="panel chart-panel">
            <h4>Gastos por categoría</h4>
            {pieData.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    paddingAngle={3}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {pieData.map((s, i) => (
                      <Cell key={i} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: 50, textAlign: "center", opacity: 0.5 }}>
                Sin datos
              </div>
            )}
          </div>

          <div className="panel trans-panel">
            <h4>Últimas transacciones</h4>
            <div className="panel-sub">Revisa tus últimas transacciones</div>
            <table className="trans-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>Descripción</th>
                  <th style={{ textAlign: "center" }}>Fecha</th>
                  <th style={{ textAlign: "right" }}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {movs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{ textAlign: "center", opacity: 0.6 }}
                    >
                      Sin movimientos
                    </td>
                  </tr>
                ) : (
                  movs.map((m) => {
                    const fecha =
                      m.createdAt instanceof Timestamp
                        ? m.createdAt.toDate()
                        : new Date(m.createdAt);
                    return (
                      <tr key={m.id}>
                        <td>{m.descripcion}</td>
                        <td>{formatDate(fecha)}</td>
                        <td className="text-right">
                          {formatCurrency(m.cantidad * m.precioUnitario)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  /* ═════════ RENDER – LISTA + TABLA ═════════ */
  return (
    <div className="container-gastos--row">
      <SidebarGastos
        ownerUid={ownerUid}
        idOrden={ordenId}
        listas={listas}
        setListas={setListas}
        onSelectLista={(l) => {
          /*  ⬇⬇⬇  MARCA SELECCIÓN MANUAL COMO ACTIVA */
          activeLista.current = l.id;
          ssSet(ssKey(ordenId, "activeLista"), l.id);

          setListaSel(l);
          if (!showList) setShowList(true);
        }}
        onListaEliminada={(id) => {
          if (id === activeLista.current) {
            activeLista.current = null;
            ssSet(ssKey(ordenId, "activeLista"), null);
          }
          if (id === listaSel?.id) setListaSel(null);
        }}
      />

      <div className="gastos-main">
        <div className="list-view">
          <div className="list-header">
            <h3 className="list-title">
              Gastos de&nbsp;<strong>{listaSel?.nombre}</strong>
            </h3>
            <button className="back-btn" onClick={() => setShowList(false)}>
              <i className="bi bi-arrow-left" /> Volver al dashboard
            </button>
          </div>

          {/* ─── TABLA ─── */}
          <table className="detalles-table">
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>#</th>
                <th>Descripción</th>
                <th>No. Parte</th>
                <th style={{ textAlign: "center" }}>Cantidad</th>
                <th>Precio unit.</th>
                <th style={{ textAlign: "center" }}>Subtotal</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productos.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", opacity: 0.6 }}>
                    Sin productos. Usa “+” para agregar uno.
                  </td>
                </tr>
              ) : (
                productos.map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td>
                      <input
                        value={p.descripcion}
                        onChange={(e) =>
                          handleChange(i, "descripcion", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={p.noParte}
                        onChange={(e) =>
                          handleChange(i, "noParte", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={p.cantidad}
                        onChange={(e) =>
                          handleChange(i, "cantidad", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={p.precioUnitario}
                        onChange={(e) =>
                          handleChange(i, "precioUnitario", e.target.value)
                        }
                      />
                    </td>
                    <td className="text-right">
                      {formatCurrency(p.cantidad * p.precioUnitario)}
                    </td>
                    <td>
                      <i
                        className="bi bi-trash-fill trash-icon"
                        onClick={() => removeRow(i)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ─── RESUMEN ─── */}
          {productos.length > 0 &&
            (() => {
              const sub = productos.reduce(
                (s, g) => s + g.cantidad * g.precioUnitario,
                0
              );
              return (
                <div
                  style={{
                    marginTop: 20,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <div
                    style={{
                      background: "#f9f9f9",
                      padding: "20px 30px",
                      border: "1px solid #ddd",
                      borderRadius: 4,
                    }}
                  >
                    <div>
                      <strong>Subtotal:</strong> {formatCurrency(sub)}
                    </div>
                    <div>
                      <strong>IVA (16 %):</strong> {formatCurrency(sub * 0.16)}
                    </div>
                    <div>
                      <strong>Total:</strong> {formatCurrency(sub * 1.16)}
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ─── FAB ─── */}
          <button className="fab" onClick={addRow}>
            +
          </button>
        </div>
      </div>
    </div>
  );
}
